import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader } from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { apiPost, apiPut } from "components/Common/Axios";
import { School } from "lib/models";
import { toast } from "sonner";

const CorrectSchoolNamePopup = ({
  open,
  onOpenChange,
  schoolName,
  setSchoolName,
  estimateId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolName: string;
  setSchoolName: (schoolName: string) => void;
  estimateId: number;
}) => {
  const [localSchoolName, setLocalSchoolName] = useState(schoolName);
  const [searchResults, setSearchResults] = useState<School[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    setSearchResults([]);
  }, [schoolName]);

  const searchSchoolName = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!localSchoolName.trim()) {
      toast.error("학교명을 입력해주세요.");
      return;
    }

    console.log("localSchoolName", localSchoolName);

    try {
      setIsSearching(true);
      const response = await apiPost("/school/search", {
        searchKeyword: localSchoolName,
      });

      if (response.error || !response.data) {
        toast.error("학교 검색에 실패했습니다.");
        setSearchResults([]);
        return;
      }

      setSearchResults(response.data);
      if (response.data.length === 0) {
        toast.info("검색 결과가 없습니다.");
      }
    } catch (error) {
      console.error("학교 검색 오류:", error);
      toast.error("학교 검색에 실패했습니다.");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSchoolSelect = async (school: School) => {
    const response = await apiPut("/estimate/school-name", {
      estimateId: estimateId,
      schoolName: school.name,
    });

    if (response.error) {
      toast.error("학교명 업데이트에 실패했습니다.");
      return;
    }
    setSchoolName(school.name);
    onOpenChange(false);
  };

  useEffect(() => {
    setLocalSchoolName(schoolName);
  }, [schoolName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] md:max-w-screen-lg max-h-[95%] overflow-x-hidden overflow-y-auto thin-scrollbar">
        <DialogHeader>
          <h2 className="text-xl font-semibold">학교명 확인</h2>
          <p className="text-sm text-gray-600">
            정확한 학교명을 검색하여 선택해주세요.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <form onSubmit={searchSchoolName} className="flex gap-2">
            <Input
              value={localSchoolName}
              onChange={(e) => setLocalSchoolName(e.target.value.trim())}
              placeholder="학교명을 입력하세요"
              className="flex-1"
            />
            <Button
              type="submit"
              disabled={isSearching}
              className="min-w-[80px]"
            >
              {isSearching ? "검색중..." : "검색"}
            </Button>
          </form>

          {/* 검색 결과 리스트 */}
          {searchResults.length > 0 && (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              <h3 className="text-sm font-medium text-gray-700">
                검색 결과 ({searchResults.length}건)
              </h3>
              {searchResults.map((school) => (
                <Card
                  key={school.id}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => handleSchoolSelect(school)}
                >
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="font-semibold text-lg text-gray-900">
                        {school.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        <div className="flex items-start gap-2">
                          <span className="font-medium min-w-[40px]">
                            주소:
                          </span>
                          <span>{school.address || "주소 정보 없음"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium min-w-[40px]">
                            전화:
                          </span>
                          <span>{school.phone || "전화번호 정보 없음"}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* 검색 결과가 없을 때 */}
          {searchResults.length === 0 &&
            !isSearching &&
            localSchoolName.trim() && (
              <div className="text-center py-8 text-gray-500">
                <p>검색 결과가 없습니다.</p>
                <p className="text-sm mt-1">다른 키워드로 검색해보세요.</p>
              </div>
            )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            취소
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CorrectSchoolNamePopup;
